import type { CookieSetOptions } from 'universal-cookie';

/**
 * Returns consistent cookie options for setting or removing the auth token.
 *
 * Domain handling:
 * - On proxibid.com subdomains (staging, QA, prod): use `.proxibid.com` so the
 *   cookie is readable across the entire domain (e.g. shared with atg-admin).
 * - On localhost (and any non-proxibid.com host): omit `domain` so the browser
 *   scopes the cookie to the current host. Setting `domain: '.proxibid.com'` on
 *   localhost is rejected by all browsers because the cookie domain must be a
 *   public-suffix match of the current hostname.
 *
 * HttpOnly: The auth token is intentionally NOT set HttpOnly here because it is
 * written by client-side JavaScript (the Login component). Browser APIs cannot set
 * HttpOnly — that flag is only honoured when the cookie arrives in a Set-Cookie
 * response header from the server.
 */

export const getAuthCookieOptions = (): CookieSetOptions => {
    const isBrowser = typeof window !== 'undefined';
    const hostname = isBrowser ? window.location.hostname : '';
    const isProxibidHost = hostname.endsWith('.proxibid.com');
    const isSecure = isBrowser && window.location.protocol === 'https:';

    return {
        ...(isProxibidHost ? { domain: '.proxibid.com' } : {}),
        path: '/',
        sameSite: 'lax' as const,
        secure: isSecure,
    };
};
